<script setup lang="ts">
import {computed, onMounted, watch, getCurrentInstance, ref} from "vue";
import {useRoute} from "vue-router";
import {useDisplay} from 'vuetify'
// track ECN to load public community information
import {useCommunityDataPublic} from '@/stores/communityDataPublic'
import {useCommunityApiStore} from '@/stores/communityApiStore'
import { FooterBar, Snackbar } from "@es-bill/es-bill-common-components";
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const route = useRoute()
const { mobile } = useDisplay();

// Check if we're in development mode
// Using MODE instead of DEV due to Vite 7 bug where DEV/PROD don't match MODE
const isDevelopment = import.meta.env.MODE === 'development';


// Available languages with labels
const availableLocales = computed(() => [
  { value: 'de', title: 'Deutsch', short: 'De' },
  { value: 'en', title: 'English', short: 'En' },
  { value: 'it', title: 'Italian', short: 'It' }
]);

// Determine which title property to use based on screen size
const languageTitleProp = computed(() => mobile.value ? 'short' : 'title');

// Computed for setting the locale
const selectedLocale = computed({
  get: () => locale.value,
  set: (value: string) => locale.value = value
});

// Footer Variables
import { version } from '../package.json'
const versionIdentifier = computed(() => getCurrentInstance()?.appContext.config.globalProperties.$appconfig?.VERSION_IDENTIFIER || '');
const copyrightText = 'so-strom';
const footerLinks = computed(() => {
  const links = [
    { text: t('impressum'), href: 'https://www.so-strom.at/impressum/' }
  ];

  if (communityContactLink.value) {
    links.push({ text: t('communityWebsite'), href: communityContactLink.value });
  }

  return links;
});
const causeOfAction = computed(() => t('causeOfAction'));

// Get community data store
const communityDataStore = useCommunityDataPublic()
// Get community API store
const communityApiStore = useCommunityApiStore()

// Add loading state
const isLoadingCommunity = ref(false);

// Create computed to access active community
const communityPublic = computed(() => communityDataStore.communitySelected);

// Community contact information for footer
const communityContactInformation = computed(() => {
  if (!communityPublic.value?.contacts) return null;
  return communityPublic.value.contacts
});

const communityContactLink = computed(() => {
  if (!communityPublic.value?.uriMoreInfo) return null;
  return communityPublic.value.uriMoreInfo
});

// track home page for displaying special header image
const homeLoaded = computed(()=> route.name === "HomeView" || route.name === "NotFoundComponent" || route.name === "ContractSigning")

// Watch for ECN changes to update selected community
watch(
  () => route.params.ecn,
  () => updateData(),
)

// Initialize on component mount
onMounted(async () => {
  // Don't load all communities, just update data for current route
  updateData();
})

//////////////////////////////////////////////////////////////////////////////////////////
async function updateData() {
  if (route.params?.ecn?.length > 0) {
    const ecn = route.params.ecn as string;

    // Set loading state
    isLoadingCommunity.value = true;

    try {
      // Fetch community directly by ECN - no need to load all communities first
      const fetchedCommunity = await communityApiStore.fetchCommunityByEcn(ecn);
      if (fetchedCommunity) {
        communityDataStore.setCommunityActive(fetchedCommunity);
      } else {
        communityDataStore.setCommunityActive(null);
      }
    } finally {
      isLoadingCommunity.value = false;
    }
  } else {
    // Clear the active community if no ECN in route
    communityDataStore.setCommunityActive(null);
    isLoadingCommunity.value = false;
  }
} // updateData

</script>


<template>

  <v-app ref="vApp" id="vApp" class="vApp">

    <!--  HEADER  -->
    <v-app-bar height="80">
      <v-row class="d-flex justify-space-between headerTitle" align="center">
        <div class="d-flex align-center">
          <router-link :to="{name: 'HomeView'}">
             <img class="logo-img" src="@/assets/sostrom/logo.svg" alt="so-strom-Logo" />
          </router-link>
          <div class="titleText">{{ t('tagline') }}</div>
        </div>
        <div class="d-flex align-center">
          <v-select
            v-if="isDevelopment"
            v-model="selectedLocale"
            :items="availableLocales"
            :item-title="languageTitleProp"
            item-value="value"
            hide-details
            class="mr-4 mt-2"
            density="compact"
            variant="outlined"
            bg-color="white"
            width="fit-content"
            prefix="🌐"
          ></v-select>
          <img v-if="communityPublic?.logoSrc" class="logo-img" :src="communityPublic?.logoSrc" alt="Gemeinschaftslogo" />
        </div>
      </v-row>
    </v-app-bar>

    <!--  MAIN  -->
    <v-main class="vMainbody">

        <v-img v-if="homeLoaded" class="imageStartBase"
               height="291"
               aspect-ratio="1/1"
               cover
               src="@/assets/sostrom/iStock-1151338370.jpg"
               alt="so-strom-intro">

          <!-- do not use v-img here -->
          <img v-if="homeLoaded" class="imageStartTop"
                 height="300"
                 width="300"

                 src="@/assets/sostrom/stern_gelb.svg"
                 alt="so-strom-stern"/>

        </v-img>

      <!-- Loading indicator for community data -->
      <div v-if="isLoadingCommunity" class="d-flex justify-center align-center pa-8">
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
        ></v-progress-circular>
      </div>

      <RouterView v-else class="routerView" :key="route.fullPath" />
      <Snackbar />
    </v-main>

    <!--  FOOOTER  -->
        <FooterBar
        :versionIdentifier="`${versionIdentifier} | ${version}`"
        :copyrightText="copyrightText"
        :footerLinks="footerLinks"
        :causeOfAction="causeOfAction"
        :communityContactInformation="communityContactInformation"
    />



  </v-app>


</template>


<style lang="scss">
.vMainbody{
--v-layout-top: 80px !important;
--v-layout-bottom: 290px !important;
background-color: rgb(var(--v-theme-background-warm));
  @media (min-width: 960px) {
   --v-layout-bottom: 180px !important;
  }
}
.background-divider {
  background-color: rgb(var(--v-theme-background-warm));
  height: 10px;
}
.footer{
  z-index: 2 !important;
}
// Typography
.textFormHeadline {
  font-size: var(--font-size-h3);
  line-height: var(--line-height-h3);
  font-weight: 500;
  padding: 16px 0 16px 0;
}

.textDescriptor {
  font-size: var(--font-size-h4);
  line-height: var(--line-height-h4);
  font-weight: 400;
  padding: 8px 0 8px 0;

  ul li {
    margin-left: 24px;
  }
}

.textDescriptorCompact {
  font-size: var(--font-size-h4);
  line-height: var(--line-height-h4);
  font-weight: 400;
}

.textDescriptorTitle {
  font-size: var(--font-size-h4);
  line-height: var(--line-height-h4);
  font-weight: 500;
}

.textHeadline {
  font-size: var(--font-size-h1);
  line-height: var(--line-height-h1);
  font-weight: 700;
  padding: 8px 0 8px 0;
}

// Form elements
.v-text-field {
  margin: 0 16px 12px 0;
}

.v-messages__message {
  margin-bottom: 4px;
}

.v-stepper-header {
  margin: 2px !important;
}

.checkboxes {
  margin-bottom: 16px;
}

.firstCheckbox {
  margin-top: 16px;
}

.v-pagination__prev,
.v-pagination__next {
  display: none !important;
}

.v-stepper-item__avatar {
  color: white !important;
  font-size: var(--font-size-caption) !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: var(--line-height-h3) !important;
}

.v-checkbox .v-label {
  opacity: 1;
  font-size: var(--font-size-h4);
  font-style: normal;
  font-weight: 400;
  line-height: var(--line-height-h5);
}

// Layout
body {
  margin: 0;
}

.v-application {
  font-size: var(--font-size-h3);
  font-style: normal;
  font-weight: 400;
  line-height: var(--line-height-h4);
}

.imageStartTop {
  float: left;
  margin-left: 110px;
  margin-top: -130px;
}

.routerView {
  background-color: rgb(var(--v-theme-white));
  margin-top: 20px;
  width: 100%;
}

.titleText {
  padding: 0 0 0 40px;
  text-align: left;
}

.vcenter {
  align-items: center;
}

.colRight {
  text-align: right;
}

// for text fields
.requiredInput label::after {
  content: "*";
}
.requiredInputCheckbox label::before {
  //content: "*";
}

// Logo and header
.logo-img {
  width: 250px;
  max-height: 60px;
  aspect-ratio: 5 / 2;
  object-fit: contain;
  @media (max-width: 959px) {
    width: 90px;
  }
}

.headerTitle {
   padding: 12px 140px 12px 100px;
  @media (max-width: 959px) {
    margin: 0px;
  }
}

// Footer
.footer {
  box-shadow: 0px -4px 4px 0px rgba(0, 0, 0, 0.10);
}

// Media queries
@media screen and (max-width: 959px) {
  .headerTitle {
    padding: 30px;
  }

  .logo-img {
    padding: 0;
  }
}

@media screen and (max-width: 800px) {
  .titleText {
    display: none;
  }
}

@media screen and (min-width: 1321px) {
  .routerView {
    max-width: 1280px !important;
  }
}

@media screen and (max-width: 1320px) {
  .routerView {
    width: calc(100% - 40px) !important;
  }
}
</style>

